import FileModal from '../FileModal';
import { NotificationModal } from 'views/components';
import PropTypes from 'prop-types';
import { fileActions } from 'core/File';
import moment from 'moment';
import { useTheme } from 'styled-components';
import { useTranslation } from 'react-i18next';
import {
    Container,
    IconContainer,
    Title,
    TreeDrawing
} from './styled';
import {
    ExpandButton,
    Input,
    MaterialIcon
} from 'mol-ui';
import React, { useEffect, useState } from 'react';
import {
    useDispatch,
    useInput,
    useSelector,
    useToggle
} from 'hooks';

const File = ({
    data,
    data: {
        created_at,
        id,
        name,
        type,
        url
    } = {},
    folderId
}) => {

    const { dangerColor } = useTheme();

    const dispatch = useDispatch();
    const { t } = useTranslation();
    const {
        blocked,
        read_only: readOnly,
        status
    } = useSelector('file');

    const [deleteModal, toggleDeleteModal] = useToggle(false);
    const [editMode, setEditMode] = useState(false);
    const [fileOpened, setFileOpened] = useState(false);
    const [newNameInput, newName, setNewName] = useInput('');

    useEffect(() => {
        if (status === `file-updated-${ id }`) {
            setEditMode(false);
            dispatch(fileActions.setFileStatus(''));
        }
    }, [status]);

    const activateEditMode = () => {
        setNewName(name);
        setEditMode(true);
    };

    const handleDelete = () => {
        const data = {
            file_id: id,
            folder_id: folderId
        };
        dispatch(fileActions.deleteFile(data));
    };

    const handleDownload = () => {
        const data = {
            id,
            name
        };
        dispatch(fileActions.downloadFile(data));
    };

    const handleOpenPDF = () => {
        window.open(url, '_blank');
    };

    const handleUpdate = () => {
        if (!!newName) {
            const data = {
                folder_id: folderId,
                id,
                name: newName
            };
            dispatch(fileActions.updateFile(data));
        }
    };

    const hasPreview = [
        'image/bmp',
        'image/gif',
        'image/jpeg',
        'image/jpg',
        'image/png'
    ].includes(type);

    return (
        <>
            <Container>
                <TreeDrawing />
                <MaterialIcon icon="insert_drive_file" size={ 18 } />
                {
                    !editMode
                        ? <Title>
                            { moment(created_at).format('DD/MM/YYYY') } - { name }
                        </Title>
                        : <>
                            <Input
                                { ...newNameInput }
                                disabled={ blocked }
                                fontSize="12px"
                                height="15px"
                                margin="0px 5px"
                                width="200px"
                                onEnterPress={ handleUpdate }
                            />
                            <ExpandButton
                                circle
                                danger
                                disabled={ blocked }
                                icon="close"
                                margin="0px 10px"
                                size={ 20 }
                                onClick={ () => setEditMode(false) }
                            />
                            <ExpandButton
                                circle
                                disabled={ !newName }
                                icon="check"
                                loading={ blocked }
                                size={ 20 }
                                onClick={ handleUpdate }
                            />
                        </>
                }
                {
                    !blocked && !editMode &&
                    <>
                        {
                            hasPreview &&
                            <IconContainer onClick={ () => setFileOpened(true) }>
                                <MaterialIcon icon="visibility" size={ 18 } />
                            </IconContainer>
                        }
                        <IconContainer onClick={ handleDownload }>
                            <MaterialIcon icon="file_download" size={ 18 } />
                        </IconContainer>
                        {
                            name.endsWith('.pdf') &&
                            <IconContainer onClick={ handleOpenPDF }>
                                <MaterialIcon icon="open_in_new" size={ 18 } />
                            </IconContainer>
                        }
                        {
                            !readOnly &&
                            <>
                                <IconContainer onClick={ activateEditMode } >
                                    <MaterialIcon icon="edit" size={ 18 } />
                                </IconContainer>
                                <IconContainer onClick={ toggleDeleteModal }>
                                    <MaterialIcon color={ dangerColor } icon="delete" size={ 18 } />
                                </IconContainer>
                            </>
                        }
                    </>
                }
            </Container>
            <FileModal
                active={ fileOpened }
                file={{ ...data, folder_id: folderId }}
                onClose={ () => setFileOpened(false) }
            />
            <NotificationModal
                extraButton
                active={ deleteModal }
                caption={ t('Eliminar') }
                extraCaption={ t('Cancelar') }
                height="fit-content"
                loading={ status === 'file-deleting' }
                title={ `${ t('¿Desea eliminar el archivo') } "${ name }"?` }
                onBack={ toggleDeleteModal }
                onSubmit={ handleDelete }
            />
        </>
    );
};

File.propTypes = {
    data: PropTypes.object,
    folderId: PropTypes.number
};

export default File;
